/**
 * Profile USP Page
 * Edit artist unique selling proposition / tagline
 * Single textarea with save/cancel and store sync
 *
 * @author Varun Pandey
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Sparkles, ArrowLeft, User } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useUspStore } from '../../stores/uspStore';
import { useArtistDetailsStore } from '@/stores/artistDetailsStore';
import OptimizedImage from '@/components/OptimizedImage';
import { useArtistUsp } from '@/api/hooks/useArtistUsp';
import { useArtistDisplayName, useArtistProfilePic } from '@/api/hooks/useArtistDisplayName';
import AchievmentRoad from './website-preview/Elite/components/AchievmentRoad';
import AchievmentMobileRoad from './website-preview/Elite/components/AchievmentMobileRoad';
import HeroFrame from './website-preview/Elite/components/HeroFrame';
import { TOAST_USP_PENDING_REVIEW } from '@/constants/profilePendingReview';
import { toastPendingReviewInfo } from '@/utils/profileReviewToast';
import { ProfileCompletionBlocksControl } from '@/components/profile/ProfileCompletionBlocksControl';
import { Loader } from '@/components/ui';
import { Sheet } from '@/components/ui/Sheet';
import { ArtistService } from '@/services/artistService';

type PlanKey = 'free' | 'economy' | 'premium' | 'elite';

const UspEliteHero: React.FC = () => {
  const { uspText, hasUsp } = useArtistUsp();
  const displayName = useArtistDisplayName();
  const profilePic = useArtistProfilePic();

  return (
    <div className="pt-5 md:pt-12 w-full border border-dashed border-white/10 rounded-xl">
      <div className="flex items-center max-md:items-start justify-around max-md:justify-between max-md:flex-col min-h-[55vh]">
        <div className="space-y-4 p-10 max-sm:p-0 rounded-3xl max-sm:px-4">
          <div>
            <p className="text-xl max-sm:text-sm flex items-end gap-4">
              <svg
                width="50"
                height="50"
                viewBox="0 0 70 50"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M62.8238 49.9205L69.1641 10.4293L46.7306 21.0456L34.5816 0L22.4298 21.0456L-0.000762939 10.4293L6.30855 49.9753L62.8238 49.9205Z"
                  fill="#893B0C"
                />
                <path
                  d="M0.851959 11.5654L22.699 21.9033L34.5822 1.32031L46.464 21.9033L68.3125 11.5654L62.2591 49.2596L6.8744 49.3144L0.851959 11.5654Z"
                  fill="url(#paint0_linear_1134_5763)"
                />
                <path
                  d="M58.2924 44.6003L62.3125 19.5642L44.5812 27.9546L34.5892 10.6465L24.5945 27.9546L6.85329 19.5571L10.8551 44.6496L58.2924 44.6003Z"
                  fill="url(#paint1_linear_1134_5763)"
                />
                <path
                  d="M58.2897 44.6011L62.2578 49.2596L6.8731 49.3144L10.8524 44.6503L58.2897 44.6011Z"
                  fill="url(#paint2_linear_1134_5763)"
                />
                <path
                  d="M46.4609 21.9028L44.5767 27.9548L34.5848 10.6467L34.5791 1.31982L46.4609 21.9028Z"
                  fill="#E8F983"
                />
                <path
                  d="M34.5803 1.32031L34.5859 10.6472L24.5912 27.9553L22.6971 21.9033L34.5803 1.32031Z"
                  fill="url(#paint3_linear_1134_5763)"
                />
                <path
                  d="M0.849783 11.5654L6.84974 19.5579L10.8516 44.6503L6.87223 49.3144L0.849783 11.5654Z"
                  fill="url(#paint4_linear_1134_5763)"
                />
                <g opacity="0.9">
                  <path
                    d="M6.85112 19.5581C6.85112 19.5581 7.00157 21.1414 7.29967 23.5065C7.46138 24.6877 7.62168 26.0685 7.84525 27.5407C8.06602 29.0129 8.30225 30.5836 8.53708 32.1542C8.80143 33.7179 9.06716 35.2857 9.31464 36.7537C9.56071 38.2217 9.83773 39.5856 10.0501 40.7583C10.5014 43.0953 10.8516 44.6505 10.8516 44.6505C10.8516 44.6505 10.7025 43.0644 10.403 40.7007C10.2413 39.521 10.0824 38.1401 9.85601 36.6679C9.63665 35.1957 9.399 33.625 9.16277 32.053C8.89842 30.488 8.63406 28.9229 8.38658 27.4535C8.1391 25.9855 7.86352 24.6216 7.64979 23.4489C7.19983 21.1119 6.85112 19.5581 6.85112 19.5581Z"
                    fill="white"
                  />
                </g>
                <path
                  d="M68.3125 11.5654L62.3111 19.5649L44.5798 27.9553L46.464 21.9033L68.3125 11.5654Z"
                  fill="url(#paint5_linear_1134_5763)"
                />
                <path
                  d="M4.86152 33.0125C6.20296 40.2611 9.08133 45.8167 11.2819 45.4132C13.4896 45.0026 14.1856 38.7861 12.8329 31.529C11.4858 24.2692 8.61309 18.729 6.40968 19.1396C4.20487 19.5446 3.51303 25.7597 4.86152 33.0125Z"
                  fill="url(#paint6_radial_1134_5763)"
                />
                <defs>
                  <linearGradient
                    id="paint0_linear_1134_5763"
                    x1="72.0673"
                    y1="20.8515"
                    x2="-20.731"
                    y2="38.271"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#AF6517" />
                    <stop offset="0.257" stopColor="#E0A03B" />
                    <stop offset="0.48" stopColor="#E9FF88" />
                    <stop offset="0.6391" stopColor="#E0A03B" />
                    <stop offset="1" stopColor="#88390B" />
                  </linearGradient>
                  <linearGradient
                    id="paint1_linear_1134_5763"
                    x1="78.964"
                    y1="-2.82834"
                    x2="-13.9634"
                    y2="67.0945"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#AF6517" />
                    <stop offset="0.257" stopColor="#E0A03B" />
                    <stop offset="0.48" stopColor="#E9FF88" />
                    <stop offset="0.6391" stopColor="#E0A03B" />
                    <stop offset="1" stopColor="#88390B" />
                  </linearGradient>
                  <linearGradient
                    id="paint2_linear_1134_5763"
                    x1="131.994"
                    y1="-69.5351"
                    x2="43.7879"
                    y2="38.0397"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#AF6517" />
                    <stop offset="0.257" stopColor="#E0A03B" />
                    <stop offset="0.48" stopColor="#E9FF88" />
                    <stop offset="0.6391" stopColor="#E0A03B" />
                    <stop offset="1" stopColor="#88390B" />
                  </linearGradient>
                  <linearGradient
                    id="paint3_linear_1134_5763"
                    x1="31.4116"
                    y1="9.69186"
                    x2="12.4176"
                    y2="54.8259"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#E0A03B" />
                    <stop offset="1" stopColor="#88390B" />
                  </linearGradient>
                  <linearGradient
                    id="paint4_linear_1134_5763"
                    x1="29.4712"
                    y1="19.5576"
                    x2="-15.5048"
                    y2="38.6679"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#AF6517" />
                    <stop offset="0.257" stopColor="#E0A03B" />
                    <stop offset="0.48" stopColor="#E9FF88" />
                    <stop offset="0.6391" stopColor="#E0A03B" />
                    <stop offset="1" stopColor="#88390B" />
                  </linearGradient>
                  <linearGradient
                    id="paint5_linear_1134_5763"
                    x1="79.4668"
                    y1="7.05133"
                    x2="45.6091"
                    y2="25.5237"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#AF6517" />
                    <stop offset="0.257" stopColor="#E0A03B" />
                    <stop offset="0.48" stopColor="#E9FF88" />
                    <stop offset="0.6391" stopColor="#E0A03B" />
                    <stop offset="1" stopColor="#88390B" />
                  </linearGradient>
                  <radialGradient
                    id="paint6_radial_1134_5763"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(8.85283 32.4634) rotate(169.467) scale(3.55628 12.2919)"
                  >
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="white" stopOpacity="0" />
                  </radialGradient>
                </defs>
              </svg>
            </p>
            <div className="flex flex-row justify-between items-center">
              <h1 className="text-5xl max-w-xs max-sm:text-2xl font-bold">
                {displayName}
              </h1>
              <div className=" z-40">
                <div className="flex flex-col justify-center items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="size-6 cursor-pointer hover:scale-[1.2] transition-all duration-300"
                  >
                    <path
                      fillRule="evenodd"
                      d="M15.75 4.5a3 3 0 1 1 .825 2.066l-8.421 4.679a3.002 3.002 0 0 1 0 1.51l8.421 4.679a3 3 0 1 1-.729 1.31l-8.421-4.678a3 3 0 1 1 0-4.132l8.421-4.679a3 3 0 0 1-.096-.755Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <p
            className={`max-w-lg max-md:max-w-sm max-sm:text-sm text-lg font-extralight opacity-80 ${!hasUsp ? 'italic' : ''
              }`}
          >
            {uspText}
          </p>

          <button className="bg-gradient-to-b from-[#EC2179] to-[#F16B2E] max-sm:text-base px-8 py-3 text-lg rounded-xl">
            SEE PRICE & BOOK
          </button>
        </div>

        <div className="w-[27rem] max-md:w-full h-[400px] relative">
          <div className="bg-[#2C2C2E] w-full max-md:w-[80%] max-md:ml-20 max-sm:w-full max-sm:ml-0 absolute h-full z-10 mt-8 flex max-sm:justify-center items-center">
            {profilePic ? (
              <OptimizedImage
                src={profilePic}
                alt={displayName}
                fill={true}
                loading="lazy"
                unoptimized={true}
                fetchPriority="high"
                className="object-cover object-top"
              />
            ) : (
              <User className="h-[40%] w-[40%] text-[#8E8E93] opacity-50" />
            )}
          </div>
          <div className="absolute top-0 right-2 w-full max-md:w-[80%] max-sm:w-full h-full flex items-center justify-center z-0">
            <HeroFrame />
          </div>
        </div>
      </div>

    </div>
  );
};

const UspPremiumHero: React.FC = () => {
  const { uspText, hasUsp } = useArtistUsp();
  const displayName = useArtistDisplayName();
  const profilePic = useArtistProfilePic();

  return (
    <div className="relative max-md:pb-10 w-full border border-dashed border-white/10 rounded-xl">
      <div className="flex lgflex-col items-center justify-around max-md:justify-between max-md:flex-col-reverse mt-5 max-md:gap-6 min-h-[60vh]">
        <div className="space-y-4 p-5 max-sm:p-0 rounded-3xl max-sm:px-4">
          <p className="text-xl  flex items-center gap-4">
            <svg
              viewBox="0 0 60 60"
              fill="none"
              className="w-12 h-12 max-sm:w-6 max-sm:h-6"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M57.6276 34.0056C57.8571 32.5276 59.12 31.1042 59.12 29.56C59.12 28.0161 57.8574 26.5924 57.6276 25.1144C57.3865 23.5963 58.1441 21.8514 57.6793 20.4223C57.2087 18.9671 55.5671 18.0031 54.8811 16.6598C54.1894 15.3023 54.3675 13.4081 53.4748 12.1827C52.5822 10.9545 50.7226 10.5411 49.6493 9.4707C48.5789 8.39739 48.1655 6.53781 46.9373 5.64518C45.7119 4.75255 43.8177 4.9306 42.4602 4.23887C41.1172 3.55292 40.1529 1.91131 38.6977 1.44071C37.2686 0.975877 35.5237 1.73352 34.0056 1.49244C32.5276 1.26291 31.1042 0 29.56 0C28.0161 0 26.5924 1.26265 25.1145 1.49244C23.5963 1.73352 21.8514 0.975877 20.4223 1.44071C18.9671 1.91131 18.0031 3.55292 16.6598 4.23887C15.3023 4.9306 13.4081 4.75255 12.1827 5.64518C10.9545 6.53781 10.5411 8.39739 9.4707 9.4707C8.39739 10.5411 6.53781 10.9545 5.64518 12.1827C4.75255 13.4081 4.93061 15.3023 4.23888 16.6598C3.55293 18.0028 1.91132 18.9671 1.44071 20.4223C0.97588 21.8514 1.73353 23.5963 1.49244 25.1144C1.26292 26.5924 0 28.0158 0 29.56C0 31.1039 1.26266 32.5276 1.49244 34.0056C1.73353 35.5237 0.97588 37.2686 1.44071 38.6977C1.91132 40.1529 3.55293 41.1169 4.23888 42.4602C4.93061 43.8177 4.75255 45.7119 5.64518 46.9373C6.53781 48.1655 8.39739 48.5789 9.4707 49.6493C10.5411 50.7226 10.9545 52.5825 12.1827 53.4748C13.4081 54.3675 15.3023 54.1894 16.6598 54.8811C18.0028 55.5671 18.9671 57.2087 20.4223 57.6793C21.8514 58.1441 23.5963 57.3865 25.1145 57.6276C26.5924 57.8571 28.0158 59.12 29.56 59.12C31.1039 59.12 32.5276 57.8574 34.0056 57.6276C35.5237 57.3865 37.2686 58.1441 38.6977 57.6793C40.1529 57.2087 41.1169 55.5671 42.4602 54.8811C43.8177 54.1894 45.7119 54.3675 46.9373 53.4748C48.1655 52.5822 48.5789 50.7226 49.6493 49.6493C50.7226 48.5789 52.5825 48.1655 53.4748 46.9373C54.3675 45.7119 54.1894 43.8177 54.8811 42.4602C55.5671 41.1172 57.2087 40.1529 57.6793 38.6977C58.1441 37.2688 57.3865 35.5237 57.6276 34.0056ZM43.9639 9.49381L50.0396 14.7543L27.3301 40.9764L17.1764 30.8227L22.8588 25.1431L26.9084 29.1897L43.9639 9.49381ZM29.56 52.7345C16.7604 52.7345 6.38576 42.3599 6.38576 29.5603C6.38576 16.7607 16.7604 6.38602 29.56 6.38602C33.5118 6.38602 37.2342 7.37608 40.4885 9.12116L38.1696 11.8017C35.5665 10.539 32.6478 9.82996 29.56 9.82996C18.663 9.82996 9.82944 18.6636 9.82944 29.5605C9.82944 40.4575 18.663 49.2911 29.56 49.2911C40.457 49.2911 49.2906 40.4575 49.2906 29.5605C49.2906 26.8141 48.7281 24.1995 47.7149 21.8234L50.18 18.9734C51.813 22.1448 52.7342 25.7463 52.7342 29.5605C52.7345 42.3599 42.3599 52.7345 29.56 52.7345Z"
                fill="url(#paint0_linear_1067_11078)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_1067_11078"
                  x1="7.61585"
                  y1="12.0803"
                  x2="51.9979"
                  y2="48.5839"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#0074D9" />
                  <stop offset="0.5" stopColor="#409EFF" />
                  <stop offset="1" stopColor="#409EFF" />
                </linearGradient>
              </defs>
            </svg>{' '}
            CELEBRITY APPEARANCE
          </p>
          <div className="flex flex-row justify-between items-center">
            <h1 className="text-5xl max-sm:text-2xl font-bold">{displayName}</h1>
            <div className=" z-40">
              <div className="flex flex-col justify-center items-center bg-white text-black p-2 rounded-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="size-6 cursor-pointer hover:scale-[1.2] transition-all duration-300"
                >
                  <path
                    fillRule="evenodd"
                    d="M15.75 4.5a3 3 0 1 1 .825 2.066l-8.421 4.679a3.002 3.002 0 0 1 0 1.51l8.421 4.679a3 3 0 1 1-.729 1.31l-8.421-4.678a3 3 0 1 1 0-4.132l8.421-4.679a3 3 0 0 1-.096-.755Z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>

          <p
            className={`max-w-lg max-sm:text-sm text-lg ${!hasUsp ? 'opacity-80 italic' : ''
              }`}
          >
            {uspText}
          </p>

          <button className="bg-gradient-to-b from-[#EC2179] to-[#F16B2E] max-sm:text-base px-8 py-3 text-lg rounded-xl">
            SEE PRICE & BOOK
          </button>
        </div>
        <div className="w-[17rem] md:w-[20rem] shadow-2xl shadow-zinc-800 h-[500px] relative rounded-[8rem] md:rounded-[10rem] overflow-hidden bg-[#2C2C2E] flex items-center justify-center mr-5">
          {profilePic ? (
            <OptimizedImage
              src={profilePic}
              alt={displayName}
              fill={true}
              loading="lazy"
              unoptimized={true}
              fetchPriority="high"
              className="object-cover object-top w-full h-full"
            />
          ) : (
            <User className="h-[40%] w-[40%] text-[#8E8E93] opacity-50" />
          )}
        </div>
      </div>
    </div>
  );
};

const UspEconomyHero: React.FC = () => {
  const { uspText, hasUsp } = useArtistUsp();
  const displayName = useArtistDisplayName();
  const profilePic = useArtistProfilePic();

  return (
    <div className="h-[55vh] relative overflow-hidden border border-dashed border-white/10 rounded-xl">

      {profilePic ? (
        <OptimizedImage
          src={profilePic}
          alt={displayName}
          loading="lazy"
          unoptimized={true}
          fetchPriority="high"
          fill={true}
          className="object-cover w-full h-full max-md:mt-16"
        />
      ) : (
        <div className="absolute inset-0 bg-[#2C2C2E] flex items-center justify-center">
          <User className="h-[40%] w-[40%] text-[#8E8E93] opacity-50" />
        </div>
      )}
      <div className="w-[150%] h-full absolute top-0 -right-20 bg-black/40 flex items-end z-10">
        <div className="w-full h-60 bg-black blur-2xl -mb-20"></div>
      </div>
      <div className="absolute bottom-8 max-sm:bottom-0 right-10 z-10 space-y-4 p-10 max-sm:p-0 rounded-3xl max-sm:px-4">
        <h1 className="text-4xl max-sm:text-2xl font-bold">{displayName}</h1>
        <p
          className={`max-w-md max-sm:text-sm ${!hasUsp ? 'opacity-90 italic' : ''
            }`}
        >
          {uspText}
        </p>
        <button className="bg-gradient-to-b from-[#EC2179] to-[#F16B2E] max-sm:text-base px-8 py-3 text-lg rounded-xl">
          SEE PRICE & BOOK
        </button>
      </div>
    </div>
  );
};

const UspFreeHero: React.FC = () => {
  const { uspText, hasUsp } = useArtistUsp();
  const displayName = useArtistDisplayName();
  const profilePic = useArtistProfilePic();

  return (
    <div className="flex flex-col justify-center md:flex-row md:justify-around items-center h-[80vh] md:px-5 lg:px-0 w-full border border-dashed border-white/10 rounded-xl">
      <div className="max-w-[22rem] w-full max-sm:w-[95%] h-[500px] relative overflow-hidden rounded-xl bg-[#2C2C2E] flex items-center justify-center lg:ml-5 mt-5">
        {profilePic ? (
          <OptimizedImage
            src={profilePic}
            alt={displayName}
            className="w-full h-full object-cover object-top"
            fill={true}
          />
        ) : (
          <User className="h-[40%] w-[40%] text-[#8E8E93] opacity-50" />
        )}
      </div>

      <div className="flex flex-col justify-start gap-[10px]  lg:max-w-[570px] h-auto text-fixed p-4">
        <p className="text-[30px] xl:text-[60px] font-semibold">
          {displayName}
        </p>
        <p
          className={`text-[14px] xl:text-[20px] font-light break-words [overflow-wrap:anywhere] ${!hasUsp ? 'opacity-80 italic' : ''
            }`}
        >
          {uspText}
        </p>
        <p className="text-[14px] xl:text-[16px] max-w-56 w-full p-[0.7rem] font-light mt-5 rounded-2xl bg-gradient-to-r from-[#F16633CC] to-[#FD2D7DCC] text-center">
          SEE PRICE & BOOK
        </p>
      </div>
    </div>
  );
};

const HERO_COMPONENTS: Record<PlanKey, React.ComponentType> = {
  free: UspFreeHero,
  economy: UspEconomyHero,
  premium: UspPremiumHero,
  elite: UspEliteHero,
};

function subscriptionNameToPlanKey(subscriptionName: string | null | undefined): PlanKey | null {
  if (!subscriptionName) return null;
  const n = subscriptionName.toLowerCase().trim();
  if (!n) return null;
  // In UserHome, "free" is displayed as "Limited Plan" (same UX concept as free tier)
  if (n.includes('limited')) return 'free';
  if (n.includes('elite')) return 'elite';
  if (n.includes('premium')) return 'premium';
  if (n.includes('economy')) return 'economy';
  if (n.includes('free')) return 'free';
  return null;
}

export const ProfileUspPage: React.FC = () => {
  const USP_MIN_LENGTH = 100;
  const USP_MAX_LENGTH = 165;
  const MAX_PLAGIARISM_ATTEMPTS = 3;
  const PLAGIARISM_SCORE_THRESHOLD = 50;
  const USP_VALIDATION_TOAST_ID = 'profile-usp-validation-toast';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data, isLoading, saveLoading, error: storeError, ensureFetched, updateUsp, setError: setStoreError } = useUspStore();
  const [usp, setUsp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isPlagiarismDrawerOpen, setIsPlagiarismDrawerOpen] = useState(false);
  const [plagiarismScore, setPlagiarismScore] = useState<number | null>(null);
  const [latestTries, setLatestTries] = useState<number | null>(null);

  const planParam = (searchParams.get('plan') ?? '').toLowerCase() as PlanKey | '';
  const artistSubscriptions = useArtistDetailsStore((s) => s.data?.subscriptions);
  const ensureArtistDetailsFetched = useArtistDetailsStore((s) => s.ensureFetched);
  const subscriptionPlan = useMemo(
    () => subscriptionNameToPlanKey(artistSubscriptions?.subscription_name),
    [artistSubscriptions?.subscription_name],
  );
  const plan: PlanKey = (planParam && planParam in HERO_COMPONENTS
    ? planParam
    : subscriptionPlan ?? 'free') as PlanKey;
  const planPreviewLabel = plan === 'free' ? 'LIMITED' : plan;
  const HeroComponent = HERO_COMPONENTS[plan];

  useEffect(() => {
    // Needed for plan detection (same source as UserHome)
    ensureArtistDetailsFetched().catch(() => undefined);
  }, [ensureArtistDetailsFetched]);

  useEffect(() => {
    ensureFetched();
  }, [ensureFetched]);

  useEffect(() => {
    if (data?.usp !== undefined) {
      const next =
        typeof data.usp === 'string' && data.usp != null
          ? data.usp
          : '';
      setUsp(next);
    }
  }, [data]);

  useEffect(() => {
    if (storeError) toast.error(storeError);
  }, [storeError]);

  useEffect(() => {
    const nextCount =
      data?.tries && typeof data.tries.count === 'number'
        ? data.tries.count
        : typeof data?.tries === 'number'
          ? data.tries
          : 0;
    setLatestTries((prev) => Math.max(prev ?? 0, nextCount));
  }, [data?.tries]);

  const getAttemptsLeft = (tries: number | null | undefined) => {
    const used = Number.isFinite(tries) ? Number(tries) : 0;
    return Math.max(0, MAX_PLAGIARISM_ATTEMPTS - used);
  };

  const getTriesCount = (tries: { status: boolean; count: number } | number | null | undefined) => {
    if (typeof tries === 'number') return tries;
    if (tries && typeof tries.count === 'number') return tries.count;
    return 0;
  };

  const showAttemptsInfoToast = () => {
    const attemptsLeft = getAttemptsLeft(latestTries ?? getTriesCount(data?.tries));
    if (attemptsLeft <= 0) {
      toast.error('Maximum number of allowed attempts reached. Please upgrade your plan to continue updating your content.');
      return;
    }
    toast(`Plagiarism attempts left: ${attemptsLeft}`, { icon: 'ℹ️' });
  };

  const showUspValidationToast = (text: string) => {
    const trimmedLength = text.trim().length;

    if (trimmedLength < USP_MIN_LENGTH) {
      const remaining = USP_MIN_LENGTH - trimmedLength;
      toast.error(
        `USP must be between ${USP_MIN_LENGTH} and ${USP_MAX_LENGTH} characters. Please add at least ${remaining} more character${remaining === 1 ? '' : 's'}.`,
        { id: USP_VALIDATION_TOAST_ID },
      );
      return;
    }

    if (trimmedLength >= USP_MAX_LENGTH) {
      toast.error(`You have reached the maximum limit of ${USP_MAX_LENGTH} characters.`, {
        id: USP_VALIDATION_TOAST_ID,
      });
      return;
    }

    toast.dismiss(USP_VALIDATION_TOAST_ID);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedUsp = usp.trim();
    const uspLength = trimmedUsp.length;

    if (uspLength < USP_MIN_LENGTH || uspLength > USP_MAX_LENGTH) {
      showUspValidationToast(usp);
      textareaRef.current?.focus();
      return;
    }
    setError(null);
    setStoreError(null);
    try {
      const plagiarismChecker = (
        ArtistService as unknown as {
          checkPlagiarism?: (text: string) => Promise<{ tries: number; plagiarism_score: number }>;
        }
      ).checkPlagiarism;

      if (typeof plagiarismChecker === 'function') {
        const plagiarismData = await plagiarismChecker(trimmedUsp);
        setLatestTries(plagiarismData.tries);

        if (plagiarismData.tries >= MAX_PLAGIARISM_ATTEMPTS) {
          toast.error('Maximum number of allowed attempts reached. Please upgrade your plan to continue updating your content.');
          return;
        }

        if (plagiarismData.plagiarism_score >= PLAGIARISM_SCORE_THRESHOLD) {
          setPlagiarismScore(plagiarismData.plagiarism_score);
          setIsPlagiarismDrawerOpen(true);
          return;
        }
      }

      await updateUsp({ usp: trimmedUsp });
      setIsEditing(false);
    } catch {
      toast.error('Failed to save. Please try again.');
    }
  };

  const maxLength = USP_MAX_LENGTH;

  const hasUspChanged = useMemo(
    () => data != null && (usp ?? '') !== (data.usp ?? ''),
    [data, usp],
  );

  const hasSavedUspContent = useMemo(
    () => typeof data?.usp === 'string' && data.usp.trim().length > 0,
    [data?.usp],
  );

  const attemptsLeft = useMemo(
    () => getAttemptsLeft(latestTries ?? getTriesCount(data?.tries)),
    [latestTries, data?.tries],
  );

  return (
    <div className="w-full bg-[#0B0B0D] text-white">
      <div className="w-full mx-auto md:px-4 md:py-6 lg:py-8 pb-16">
        <header className="mb-6 lg:mb-8">
          <div className="mb-5 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex min-h-[44px] shrink-0 items-center gap-2 -ml-2 rounded-xl pl-2 pr-3 text-[#8E8E93] transition-colors hover:bg-[#1C1C1E] hover:text-white active:bg-[#2C2C2E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D]"
            >
              <ArrowLeft className="h-5 w-5 shrink-0" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <ProfileCompletionBlocksControl />
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-10 w-10 lg:h-12 lg:w-12 shrink-0 items-center justify-center rounded-xl bg-[#1C1C1E] border border-[#3A3A3C]">
                <Sparkles className="h-5 w-5 lg:h-6 lg:w-6 text-[#8E8E93]" />
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight uppercase tracking-widest">
                  USP
                </h1>
                <p className="text-[#8E8E93] text-sm lg:text-base mt-0.5">
                  Tell clients what makes you truly unique
                </p>
              </div>
            </div>
            <div className="flex justify-end w-full sm:w-auto flex-shrink-0 gap-2">
              {isEditing ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditing(false);
                      if (data?.usp !== undefined) {
                        setUsp(typeof data.usp === 'string' && data.usp != null ? data.usp : '');
                      }
                    }}
                    className="inline-flex items-center gap-1.5 rounded-full bg-transparent text-[#E5E5EA] border border-[#3A3A3C] px-4 py-2 text-xs font-medium hover:bg-[#1C1C1E] hover:text-white transition-colors text-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="usp-form"
                    disabled={!hasUspChanged || saveLoading || isLoading}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white text-black border border-[#3A3A3C] px-4 py-2 text-xs font-medium hover:bg-white/90 hover:text-black transition-colors text-nowrap disabled:opacity-50 disabled:pointer-events-none disabled:bg-[#3A3A3C] disabled:text-[#8E8E93]"
                  >
                    {saveLoading ? 'Saving…' : 'Save'}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (data?.pending === true) {
                      toastPendingReviewInfo(TOAST_USP_PENDING_REVIEW);
                      return;
                    }
                    setIsEditing(true);
                  }}
                  disabled={isLoading}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white text-black border border-[#3A3A3C] px-4 py-2 text-xs font-medium hover:bg-white/90 hover:text-black transition-colors text-nowrap disabled:opacity-50 disabled:pointer-events-none disabled:bg-[#3A3A3C] disabled:text-[#8E8E93]"
                >
                  {hasSavedUspContent ? 'Update USP' : 'Add USP'}
                </button>
              )}
            </div>
          </div>
        </header>
        {!isEditing && (isLoading || saveLoading) && (
          <div className='flex items-center justify-center'>
            <Loader size="small" />
          </div>


        )}
        {!isEditing && !(isLoading || saveLoading) && hasSavedUspContent && <div className="mb-4 rounded-xl border border-[#2C2C2E] bg-[#141416] px-4 py-3 text-xs text-[#A0A0A4] flex items-center justify-between gap-3">
          <span>
            This is a live preview of how your USP section will appear on your public website.
          </span>
          <span className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-[#E5E5EA]">
            Plan: {planPreviewLabel}
          </span>
        </div>}
        {!isEditing && !(isLoading || saveLoading) && !hasSavedUspContent && (
          <div className="mb-4 rounded-xl border border-[#2C2C2E] bg-[#141416] px-4 py-3 text-xs text-[#A0A0A4]">
            Your USP is not added yet. Please add it using the <span className="font-medium text-[#E5E5EA]">Add USP</span> button.
          </div>
        )}
        {!isEditing && !(isLoading || saveLoading) && hasSavedUspContent && (
          <div className="mb-8">
            <HeroComponent />

          </div>
        )}

        {isEditing && (
          <motion.form
            id="usp-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
            className="space-y-6"
          >
            <section className="rounded-2xl  space-y-4">
              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <label className="block text-sm font-medium text-white capitalize tracking-wide">
                    Your unique selling proposition
                  </label>
                  <button
                    type="button"
                    onClick={showAttemptsInfoToast}
                    className="text-xs font-medium text-yellow-400 hover:text-yellow-300 transition-colors"
                  >
                    Plagiarism attempts left: {attemptsLeft}
                  </button>
                </div>
                <div className="relative">
                  <textarea
                    ref={textareaRef}
                    value={usp}
                    disabled={isLoading}
                    maxLength={USP_MAX_LENGTH}
                    onChange={(e) => {
                      const next = e.target.value.slice(0, maxLength);
                      setUsp(next);
                      if (error) setError(null);
                      if (storeError) setStoreError(null);
                    }}
                    rows={6}
                    placeholder="Describe what sets you apart – style, experience, energy, crowd engagement, or anything that makes you special."
                    className={cn(
                      'w-full px-4 py-3 bg-transparent border rounded-xl text-white placeholder-[#8E8E93]',
                      'focus:outline-none focus:ring-2 focus:ring-[#F33363] focus:border-transparent',
                      'resize-none transition-all',
                      'border-[#3A3A3C]',
                    )}
                  />
                  <div className="absolute bottom-3 right-3 text-xs text-[#8E8E93]">
                    {(usp ?? '').length}/{maxLength}
                  </div>
                </div>
              </div>
            </section>
          </motion.form>
        )}
      </div>
      <Sheet
        open={isPlagiarismDrawerOpen}
        onOpenChange={setIsPlagiarismDrawerOpen}
        title="Your content is highly plagiarized! Please rephrase it."
      >
        <div className="space-y-3 text-sm text-[#E5E5EA]">
          <p className="text-yellow-400 font-medium">
            Plagiarism score: {plagiarismScore ?? 0}%
          </p>
          <p>
            Your content matches existing sources. Please revise it to ensure originality.
          </p>
        </div>
      </Sheet>
    </div>
  );
};

